import { useRef, useState, useEffect } from "react"

const DualRangeSlider = ({
  min = 0,
  max = 100,
  valueMin,
  valueMax,
  onChange,
  precision = 2,
  unit = "%"
}) => {

  const trackRef = useRef(null)
  const frameRef = useRef(null)
  const [dragging, setDragging] = useState(null)

  const getPercent = v => ((v - min) / (max - min)) * 100

  const handleMove = e => {
    if (!dragging) return

    if (frameRef.current) cancelAnimationFrame(frameRef.current)

    frameRef.current = requestAnimationFrame(() => {
      const rect = trackRef.current.getBoundingClientRect()
      const px = e.clientX - rect.left
      const ratio = Math.min(Math.max(px / rect.width, 0), 1)

      const raw = min + ratio * (max - min)
      const newVal = Number(raw.toFixed(precision))

      if (dragging === "min" && newVal <= valueMax) {
        onChange({ min: newVal, max: valueMax })
      }

      if (dragging === "max" && newVal >= valueMin) {
        onChange({ min: valueMin, max: newVal })
      }
    })
  }

  const stopDrag = () => {
    setDragging(null)
    if (frameRef.current) cancelAnimationFrame(frameRef.current)
  }

  useEffect(() => {
    window.addEventListener("pointermove", handleMove)
    window.addEventListener("pointerup", stopDrag)
    window.addEventListener("pointercancel", stopDrag)

    return () => {
      window.removeEventListener("pointermove", handleMove)
      window.removeEventListener("pointerup", stopDrag)
      window.removeEventListener("pointercancel", stopDrag)
    }
  })

  const handleTrackPointerDown = e => {
    const rect = trackRef.current.getBoundingClientRect()
    const px = e.clientX - rect.left
    const ratio = Math.min(Math.max(px / rect.width, 0), 1)

    const raw = min + ratio * (max - min)
    const newVal = Number(raw.toFixed(precision))

    const distMin = Math.abs(newVal - valueMin)
    const distMax = Math.abs(newVal - valueMax)

    if (distMin < distMax) {
      onChange({ min: newVal, max: valueMax })
      setDragging("min")
    } else {
      onChange({ min: valueMin, max: newVal })
      setDragging("max")
    }
  }

  return (
    <div className="w-full mb-4">
      <div className="flex justify-between mb-2 text-sm text-gray-600">
        <span>{valueMin.toFixed(precision)}{unit}</span>
        <span>{valueMax.toFixed(precision)}{unit}</span>
      </div>

      <div
        className="relative h-3 rounded bg-gray-200 w-[96%] left-[2%] touch-none"
        ref={trackRef}
        onPointerDown={handleTrackPointerDown}
      >
        <div
          className="absolute top-1/2 -translate-y-1/2 h-3 bg-[#4b5985] rounded"
          style={{
            left: `${getPercent(valueMin)}%`,
            width: `${getPercent(valueMax) - getPercent(valueMin)}%`
          }}
        />

        <div
          className="absolute top-1/2 w-5 h-5 rounded-full bg-white border border-gray-400 shadow cursor-pointer"
          style={{
            left: `${getPercent(valueMin)}%`,
            transform: "translate(-50%, -50%)"
          }}
          onPointerDown={e => {
            e.stopPropagation()
            setDragging("min")
          }}
        />

        <div
          className="absolute top-1/2 w-5 h-5 rounded-full bg-white border border-gray-400 shadow cursor-pointer"
          style={{
            left: `${getPercent(valueMax)}%`,
            transform: "translate(-50%, -50%)"
          }}
          onPointerDown={e => {
            e.stopPropagation()
            setDragging("max")
          }}
        />
      </div>
    </div>
  )
}

export default DualRangeSlider